import { FunctionComponent } from "react";

import { format } from "date-fns";

import {
	faFacebook,
	faInstagram,
	faPinterest,
	faSnapchat,
	faTiktok,
	faXTwitter,
	faYelp,
	faYoutube,
} from "@fortawesome/free-brands-svg-icons";

import {
	faChevronDoubleDown,
	faChevronDoubleUp,
	IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";

import { useTranslation } from "react-i18next";

import { TFunction } from "i18next";

import { BrandsIcon } from "@/components/BrandsIcon/BrandsIcon";
import { Icon } from "@/components";

import { ListPerson } from "../../../schema/list";
import { LeadInfoBox } from "../../../../Messages/components/LeadInfoBox";

type LiPostResult = NonNullable<ListPerson["liPostResult"]>;
type Offer = NonNullable<ListPerson["offer"]>;
type LiEventEntry = NonNullable<ListPerson["liEventEntry"]>;
type MapBusiness = NonNullable<ListPerson["mapBusiness"]>;

interface ListSourceDataSectionProps {
	listPerson: ListPerson;
}

export const ListSourceDataSection: FunctionComponent<ListSourceDataSectionProps> = ({
	listPerson,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.${key}`);

	const hasContent =
		!!listPerson.liPostResult ||
		!!listPerson.offer ||
		!!listPerson.liEventEntry ||
		!!listPerson.liCompanyActivityEntryResult?.liCompanyActivityEntry ||
		!!listPerson.mapBusiness;

	if (!hasContent) {
		return null;
	}

	return (
		<details
			className="flex group w-full flex-col border border-gray-200 rounded-lg p-4"
			open={false}
		>
			<summary className="cursor-pointer flex bg-white flex-row items-start w-full justify-between gap-2">
				<h3 className="text-sm font-semibold text-gray-900">{t("inbox.sourceData")}</h3>
				<div>
					<div className="flex flex-row gap-2 justify-center items-center text-sm group-open:hidden">
						<p>{ts("show.showMore")}</p>
						<Icon className="cursor-pointer text-sm w-3 h-3" icon={faChevronDoubleDown} />
					</div>
					<div className="flex flex-row gap-2 justify-center items-center text-sm hidden group-open:flex">
						<p>{ts("show.showLess")}</p>
						<Icon className="cursor-pointer text-sm w-3 h-3" icon={faChevronDoubleUp} />
					</div>
				</div>
			</summary>
			<div className="flex flex-col gap-2 mt-4">
				{listPerson.liPostResult && <LiPostSection liPostResult={listPerson.liPostResult} t={t} />}
				{listPerson.offer && <OfferSection offer={listPerson.offer} t={t} />}
				{listPerson.liEventEntry && <LiEventSection liEventEntry={listPerson.liEventEntry} t={t} />}
				{listPerson.liCompanyActivityEntryResult?.liCompanyActivityEntry && (
					<LeadInfoBox
						content={
							<div className="text-sm text-text-meta font-semibold flex flex-row items-center justify-start gap-1">
								<span>
									{
										listPerson.liCompanyActivityEntryResult.liCompanyActivityEntry.liCompanyActivity
											.name
									}
								</span>
							</div>
						}
						url={listPerson.liCompanyActivityEntryResult.liCompanyActivityEntry.linkedinUrl ?? ""}
					/>
				)}
				{listPerson.mapBusiness && (
					<MapBusinessSection mapBusiness={listPerson.mapBusiness} t={t} />
				)}
			</div>
		</details>
	);
};

const LiPostSection: FunctionComponent<{ liPostResult: LiPostResult; t: TFunction }> = ({
	liPostResult,
	t,
}) => (
	<LeadInfoBox
		content={
			<div className="flex flex-col gap-1">
				<div className="flex gap-2">
					<h5 className="font-semibold">{t("liProfile.type")}:</h5>
					<p>{liPostResult.type ? t(`inbox.${liPostResult.type}`) : ""}</p>
				</div>
				<div className="flex gap-2">
					<h5 className="font-semibold">{t("liProfile.engagement")}:</h5>
					<p>
						{liPostResult?.likes} {t("inbox.likes")}, {liPostResult?.comments} {t("inbox.comments")}
						, {liPostResult?.shares} {t("inbox.shares")}
					</p>
				</div>
				{liPostResult.contentResponse && (
					<div className="flex flex-col wrap-break-word mb-2">
						<h5 className="font-semibold flex-wrap">{t("inbox.comment")}</h5>
						<p className="wrap-break-word max-w-full">{liPostResult.contentResponse ?? ""}</p>
					</div>
				)}
				<div className="flex flex-col wrap-break-word">
					<h5 className="font-semibold flex-wrap">{t("liProfile.post")}</h5>
					<p className="wrap-break-word max-w-full">{liPostResult.content ?? ""}</p>
				</div>
			</div>
		}
		url={liPostResult.contentUrl ?? ""}
	/>
);

const OfferSection: FunctionComponent<{ offer: Offer; t: TFunction }> = ({ offer, t }) => (
	<LeadInfoBox
		content={
			<>
				{offer.jobTitle && (
					<div className="flex gap-2 flex-wrap w-full">
						<h5 className="font-semibold min-w-fit">{t("inbox.jobTitle")}:</h5>
						<p>{offer.jobTitle}</p>
					</div>
				)}
				{offer.postedAt && (
					<div className="flex gap-2">
						<h5 className="font-semibold">{t("inbox.postedAt")}:</h5>
						<p>{format(new Date(offer.postedAt as unknown as string), "MMM d, yyyy")}</p>
					</div>
				)}
				{offer.employmentType && (
					<div className="flex gap-2">
						<h5 className="font-semibold">{t("inbox.employmentType")}:</h5>
						<p>{offer.employmentType ?? ""}</p>
					</div>
				)}
				<div>
					<h5 className="font-semibold">{t("inbox.jobDescription")}:</h5>
					<p>{offer.description ?? ""}</p>
				</div>
			</>
		}
		url={offer.offerLink ?? ""}
	/>
);

const LiEventSection: FunctionComponent<{ liEventEntry: LiEventEntry; t: TFunction }> = ({
	liEventEntry,
	t,
}) => (
	<LeadInfoBox
		content={
			<div>
				<div className="text-sm text-text mb-2 flex gap-2">
					<h5 className="font-semibold min-w-fit">{t("inbox.event")}:</h5>
					<p>{liEventEntry.name}</p>
				</div>
				<div className="flex gap-2">
					<h5 className="font-semibold min-w-fit">{t("inbox.evenAt")}:</h5>
					<p>{format(new Date(liEventEntry.eventAt as string), "MMM d, yyyy")}</p>
				</div>
				<div className="flex gap-2">
					<h5 className="font-semibold min-w-fit">{t("inbox.eventAttendees")}:</h5>
					<p>{liEventEntry.attendees}</p>
				</div>
				{liEventEntry.description && (
					<div className="flex flex-col gap-2">
						<h5 className="font-semibold min-w-fit">{t("inbox.eventDescription")}</h5>
						<p>{liEventEntry.description}</p>
					</div>
				)}
			</div>
		}
		url={liEventEntry.url ?? ""}
	/>
);

const SocialMediaLink: FunctionComponent<{
	icon: IconDefinition;
	label: string;
	url: string;
	companyName?: string | null;
}> = ({ icon, label, url, companyName }) => (
	<div className="flex gap-2 items-center">
		<BrandsIcon icon={icon} />
		<h5 className="font-semibold">{label}: </h5>
		<a href={url} rel="noreferrer" target="_blank">
			<div className="flex flex-row gap-1 items-center justify-center">{companyName}</div>
		</a>
	</div>
);

const MapBusinessSection: FunctionComponent<{ mapBusiness: MapBusiness; t: TFunction }> = ({
	mapBusiness,
	t,
}) => (
	<LeadInfoBox
		content={
			<div className="min-w-full">
				<div className="flex gap-2">
					<h5 className="font-semibold">{t("inbox.rating")}: </h5>
					<p>{mapBusiness.rating ?? "No reviews"}</p>
				</div>
				<div className="flex gap-2">
					<h5 className="font-semibold">{t("inbox.reviewCount")}: </h5>
					<p>{mapBusiness.reviewCount ? mapBusiness.reviewCount : "None"}</p>
				</div>
				{mapBusiness.subtypes && (
					<div className="flex gap-2">
						<h5 className="font-semibold">{t("inbox.businessType")}: </h5>
						<p>{mapBusiness.subtypes}</p>
					</div>
				)}
				{mapBusiness.website && (
					<div className="flex gap-2 mb-2">
						<h5 className="font-semibold">{t("inbox.website")}: </h5>
						<a href={mapBusiness.website} rel="noreferrer" target="_blank">
							{mapBusiness.website}
						</a>
					</div>
				)}
				{mapBusiness.facebookUrl && (
					<SocialMediaLink
						companyName={mapBusiness.companyName}
						icon={faFacebook as IconDefinition}
						label="Facebook"
						url={mapBusiness.facebookUrl}
					/>
				)}
				{mapBusiness.instagramUrl && (
					<SocialMediaLink
						companyName={mapBusiness.companyName}
						icon={faInstagram as IconDefinition}
						label="Instagram"
						url={mapBusiness.instagramUrl}
					/>
				)}
				{mapBusiness.twitterUrl && (
					<SocialMediaLink
						companyName={mapBusiness.companyName}
						icon={faXTwitter as IconDefinition}
						label="Twitter/X"
						url={mapBusiness.twitterUrl}
					/>
				)}
				{mapBusiness.tiktokUrl && (
					<SocialMediaLink
						companyName={mapBusiness.companyName}
						icon={faTiktok as IconDefinition}
						label="Tiktok"
						url={mapBusiness.tiktokUrl}
					/>
				)}
				{mapBusiness.pinterestUrl && (
					<SocialMediaLink
						companyName={mapBusiness.companyName}
						icon={faPinterest as IconDefinition}
						label="Pinterest"
						url={mapBusiness.pinterestUrl}
					/>
				)}
				{mapBusiness.yelpUrl && (
					<SocialMediaLink
						companyName={mapBusiness.companyName}
						icon={faYelp as IconDefinition}
						label="Yelp"
						url={mapBusiness.yelpUrl}
					/>
				)}
				{mapBusiness.snapchatUrl && (
					<SocialMediaLink
						companyName={mapBusiness.companyName}
						icon={faSnapchat as IconDefinition}
						label="Snapchat"
						url={mapBusiness.snapchatUrl}
					/>
				)}
				{mapBusiness.youtubeUrl && (
					<SocialMediaLink
						companyName={mapBusiness.companyName}
						icon={faYoutube as IconDefinition}
						label="Youtube"
						url={mapBusiness.youtubeUrl}
					/>
				)}
			</div>
		}
		url={mapBusiness.placeLink ?? ""}
	/>
);
